/**
 * Dance Music Player PWA - Main Application Orchestrator
 * Integrates File System Access API, IndexedDB persistence, differential indexing,
 * dynamic tag management and a real-time visualizer.
 */

import type { Track, Tag, RepeatMode, ViewMode, SortType, IndexingStatus, AppState, TracksApiResponse } from './types.ts';
import { escapeHtml, formatTime, formatFileSize, showToast } from './utils.ts';
import { dbManager } from './db.ts';
import { directoryIndexer } from './indexer.ts';
import { getDeterministicGradient } from './metadata.ts';
import { AudioVisualizer } from './visualizer.ts';
import { KeyboardShortcutsManager } from './shortcuts.ts';

interface InstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export class DancePlayerApp {
    // Application State
    public allTracks: Track[] = [];
    public displayedTracks: Track[] = [];
    public tags: Tag[] = [];
    public currentTrackIndex: number = -1;
    public currentTrackId: string | null = null;
    public isPlaying: boolean = false;
    public playbackRate: number = 1.0;
    public repeatMode: RepeatMode = 'off';
    public isShuffle: boolean = false;
    public volume: number = 0.8;
    public prevVolume: number = 0.8;
    public isMuted: boolean = false;
    public selectedTagId: string = 'all';
    public playbackTagId: string = 'all';
    public activeSort: SortType = 'default';
    public viewMode: ViewMode = 'list';
    public directoryHandle: FileSystemDirectoryHandle | null = null;

    // Resumption & State flags
    private currentBlobUrl: string | null = null;
    private saveStateDebounceTimer: number | null = null;
    private isRestoringSeek: boolean = false;
    private targetSeekTime: number = 0;
    private shouldResumeOnFirstInteraction: boolean = false;
    private installPrompt: InstallPromptEvent | null = null;

    // Subsystems
    public visualizer!: AudioVisualizer;
    public shortcuts!: KeyboardShortcutsManager;

    // Core Audio Element
    public audio: HTMLAudioElement;

    // DOM Elements - Top & Nav
    public searchInput!: HTMLInputElement;
    public clearSearchBtn!: HTMLButtonElement;
    public totalTracksBadge!: HTMLElement;
    public favTracksBadge!: HTMLElement;
    public viewListBtn!: HTMLButtonElement;
    public viewGridBtn!: HTMLButtonElement;
    public sortSelect!: HTMLSelectElement;
    public btnSelectDir!: HTMLButtonElement;
    public btnSelectDirLabel!: HTMLElement;
    public btnSyncDir!: HTMLButtonElement;
    public btnInstallApp!: HTMLButtonElement;
    public indexingBanner!: HTMLElement;
    public indexingProgressBar!: HTMLElement;
    public indexingStatusText!: HTMLElement;

    // DOM Elements - Main Containers & Views
    public tracksTableBody!: HTMLTableSectionElement;
    public tracksTable!: HTMLTableElement;
    public tracksGrid!: HTMLDivElement;
    public emptyState!: HTMLDivElement;
    public emptyMessage!: HTMLElement;
    public btnClearSearchEmpty!: HTMLButtonElement;
    public heroTrackCount!: HTMLElement;
    public heroPlaylistTitle!: HTMLElement;
    public heroCover!: HTMLElement;
    public tagNavList!: HTMLUListElement;

    // DOM Elements - Player Left
    public playerCover!: HTMLElement;
    public playerTitle!: HTMLElement;
    public playerArtist!: HTMLElement;
    public btnPlayerTrackJump!: HTMLButtonElement;
    public btnPlayerFav!: HTMLButtonElement;

    // DOM Elements - Player Center Controls
    public btnPlayPause!: HTMLButtonElement;
    public iconPlay!: SVGElement;
    public iconPause!: SVGElement;
    public btnPrev!: HTMLButtonElement;
    public btnNext!: HTMLButtonElement;
    public btnRewind5!: HTMLButtonElement;
    public btnRewind10!: HTMLButtonElement;
    public btnForward5!: HTMLButtonElement;
    public btnForward10!: HTMLButtonElement;
    public btnShuffle!: HTMLButtonElement;
    public btnRepeat!: HTMLButtonElement;
    public repeatOneBadge!: HTMLElement;
    public btnPlayAll!: HTMLButtonElement;
    public btnHeroShuffle!: HTMLButtonElement;
    public btnAddTracksToTag!: HTMLButtonElement;

    // DOM Elements - Scrubber & Timeline
    public timeCurrent!: HTMLElement;
    public timeTotal!: HTMLElement;
    public progressContainer!: HTMLElement;
    public progressFill!: HTMLElement;
    public scrubTooltip!: HTMLElement;

    // DOM Elements - Player Right (Speed & Volume)
    public btnSpeedMenu!: HTMLButtonElement;
    public playerSpeedLabel!: HTMLElement;
    public speedPopup!: HTMLElement;
    public speedSlider!: HTMLInputElement;
    public btnSpeedMinus!: HTMLButtonElement;
    public btnSpeedPlus!: HTMLButtonElement;
    public speedStepValue!: HTMLElement;
    public btnResetSpeedMini!: HTMLButtonElement;
    public btnVolume!: HTMLButtonElement;
    public iconVolHigh!: SVGElement;
    public iconVolMute!: SVGElement;
    public volumeBarContainer!: HTMLElement;
    public volumeFill!: HTMLElement;
    public btnFullscreen!: HTMLButtonElement;

    // DOM Elements - Modals
    public shortcutsModal!: HTMLElement;
    public btnOpenShortcuts!: HTMLButtonElement;
    public btnCloseShortcuts!: HTMLButtonElement;
    public btnModalGotit!: HTMLButtonElement;

    // Tag Editor Modal
    public tagModal!: HTMLElement;
    public btnAddTag!: HTMLButtonElement;
    public btnCloseTagModal!: HTMLButtonElement;
    public tagModalTitle!: HTMLElement;
    public tagInputName!: HTMLInputElement;
    public tagInputColor!: HTMLInputElement;
    public btnSaveTag!: HTMLButtonElement;
    private editingTagId: string | null = null;

    // Track Editor Modal
    public trackEditModal!: HTMLElement;
    public trackEditTitle!: HTMLInputElement;
    public trackEditBpm!: HTMLInputElement;
    public trackEditTags!: HTMLElement;
    public trackEditSelectedTags!: HTMLElement;
    public trackEditTagSearch!: HTMLInputElement;
    public btnCloseTrackEdit!: HTMLButtonElement;
    public btnCancelTrackEdit!: HTMLButtonElement;
    public btnSaveTrackEdit!: HTMLButtonElement;
    private editingTrackId: string | null = null;
    private editingTrackTags: Set<string> = new Set();

    // Add Tracks Modal
    public addTracksModal!: HTMLElement;
    public addTracksModalTitle!: HTMLElement;
    public addTracksSearch!: HTMLInputElement;
    public addTracksSummary!: HTMLElement;
    public addTracksResults!: HTMLElement;
    public btnCloseAddTracks!: HTMLButtonElement;

    constructor() {
        this.audio = document.getElementById('audio-element') as HTMLAudioElement;
        this.initElements();
        this.setupWindowControlsOverlay();
        this.initSubsystems();
        this.bindEvents();
        this.setupAudioListeners();
        this.registerServiceWorker();
        this.initializeApp();
    }

    private setupWindowControlsOverlay(): void {
        const navigatorWithOverlay = navigator as Navigator & {
            windowControlsOverlay?: {
                visible: boolean;
                addEventListener(type: 'geometrychange', listener: () => void): void;
            };
        };
        const overlay = navigatorWithOverlay.windowControlsOverlay;
        const update = (): void => {
            document.body.classList.toggle('window-controls-overlay', overlay?.visible === true);
        };
        update();
        overlay?.addEventListener('geometrychange', update);
    }

    private initElements(): void {
        this.searchInput = document.getElementById('search-input') as HTMLInputElement;
        this.clearSearchBtn = document.getElementById('clear-search-btn') as HTMLButtonElement;
        this.totalTracksBadge = document.getElementById('total-tracks-count') as HTMLElement;
        this.favTracksBadge = document.getElementById('fav-tracks-count') as HTMLElement;
        this.viewListBtn = document.getElementById('view-list-btn') as HTMLButtonElement;
        this.viewGridBtn = document.getElementById('view-grid-btn') as HTMLButtonElement;
        this.sortSelect = document.getElementById('sort-select') as HTMLSelectElement;
        this.btnSelectDir = document.getElementById('btn-select-dir') as HTMLButtonElement;
        this.btnSelectDirLabel = document.getElementById('btn-select-dir-label') as HTMLElement;
        this.btnSyncDir = document.getElementById('btn-sync-dir') as HTMLButtonElement;
        this.btnInstallApp = document.getElementById('btn-install-app') as HTMLButtonElement;

        this.indexingBanner = document.getElementById('indexing-banner') as HTMLElement;
        this.indexingProgressBar = document.getElementById('indexing-progress-bar') as HTMLElement;
        this.indexingStatusText = document.getElementById('indexing-status-text') as HTMLElement;

        this.tracksTableBody = document.getElementById('tracks-table-body') as HTMLTableSectionElement;
        this.tracksTable = document.getElementById('tracks-table') as HTMLTableElement;
        this.tracksGrid = document.getElementById('tracks-grid') as HTMLDivElement;
        this.emptyState = document.getElementById('empty-state') as HTMLDivElement;
        this.emptyMessage = document.getElementById('empty-message') as HTMLElement;
        this.btnClearSearchEmpty = document.getElementById('btn-clear-search-empty') as HTMLButtonElement;
        this.heroTrackCount = document.getElementById('hero-track-count') as HTMLElement;
        this.heroPlaylistTitle = document.getElementById('hero-playlist-title') as HTMLElement;
        this.heroCover = document.getElementById('hero-cover') as HTMLElement;
        this.tagNavList = document.getElementById('tag-nav-list') as HTMLUListElement;

        this.playerCover = document.getElementById('player-cover') as HTMLElement;
        this.playerTitle = document.getElementById('player-title') as HTMLElement;
        this.playerArtist = document.getElementById('player-artist') as HTMLElement;
        this.btnPlayerTrackJump = document.getElementById('btn-player-track-jump') as HTMLButtonElement;
        this.btnPlayerFav = document.getElementById('btn-player-fav') as HTMLButtonElement;

        this.btnPlayPause = document.getElementById('btn-play-pause') as HTMLButtonElement;
        this.iconPlay = document.getElementById('icon-play') as unknown as SVGElement;
        this.iconPause = document.getElementById('icon-pause') as unknown as SVGElement;
        this.btnPrev = document.getElementById('btn-prev') as HTMLButtonElement;
        this.btnNext = document.getElementById('btn-next') as HTMLButtonElement;
        this.btnRewind5 = document.getElementById('btn-rewind-5') as HTMLButtonElement;
        this.btnRewind10 = document.getElementById('btn-rewind-10') as HTMLButtonElement;
        this.btnForward5 = document.getElementById('btn-forward-5') as HTMLButtonElement;
        this.btnForward10 = document.getElementById('btn-forward-10') as HTMLButtonElement;
        this.btnShuffle = document.getElementById('btn-shuffle') as HTMLButtonElement;
        this.btnRepeat = document.getElementById('btn-repeat') as HTMLButtonElement;
        this.repeatOneBadge = document.getElementById('repeat-one-badge') as HTMLElement;
        this.btnPlayAll = document.getElementById('btn-play-all') as HTMLButtonElement;
        this.btnHeroShuffle = document.getElementById('btn-hero-shuffle') as HTMLButtonElement;
        this.btnAddTracksToTag = document.getElementById('btn-add-tracks-to-tag') as HTMLButtonElement;

        this.timeCurrent = document.getElementById('time-current') as HTMLElement;
        this.timeTotal = document.getElementById('time-total') as HTMLElement;
        this.progressContainer = document.getElementById('progress-container') as HTMLElement;
        this.progressFill = document.getElementById('progress-fill') as HTMLElement;
        this.scrubTooltip = document.getElementById('scrub-tooltip') as HTMLElement;

        this.btnSpeedMenu = document.getElementById('btn-speed-menu') as HTMLButtonElement;
        this.playerSpeedLabel = document.getElementById('player-speed-label') as HTMLElement;
        this.speedPopup = document.getElementById('speed-popup') as HTMLElement;
        this.speedSlider = document.getElementById('speed-slider') as HTMLInputElement;
        this.btnSpeedMinus = document.getElementById('btn-speed-minus') as HTMLButtonElement;
        this.btnSpeedPlus = document.getElementById('btn-speed-plus') as HTMLButtonElement;
        this.speedStepValue = document.getElementById('speed-step-value') as HTMLElement;
        this.btnResetSpeedMini = document.getElementById('btn-reset-speed-mini') as HTMLButtonElement;
        this.btnVolume = document.getElementById('btn-volume') as HTMLButtonElement;
        this.iconVolHigh = document.getElementById('icon-vol-high') as unknown as SVGElement;
        this.iconVolMute = document.getElementById('icon-vol-mute') as unknown as SVGElement;
        this.volumeBarContainer = document.getElementById('volume-bar-container') as HTMLElement;
        this.volumeFill = document.getElementById('volume-fill') as HTMLElement;
        this.btnFullscreen = document.getElementById('btn-fullscreen') as HTMLButtonElement;

        this.shortcutsModal = document.getElementById('shortcuts-modal') as HTMLElement;
        this.btnOpenShortcuts = document.getElementById('btn-open-shortcuts') as HTMLButtonElement;
        this.btnCloseShortcuts = document.getElementById('btn-close-shortcuts') as HTMLButtonElement;
        this.btnModalGotit = document.getElementById('btn-modal-gotit') as HTMLButtonElement;

        this.tagModal = document.getElementById('tag-modal') as HTMLElement;
        this.btnAddTag = document.getElementById('btn-add-tag') as HTMLButtonElement;
        this.btnCloseTagModal = document.getElementById('btn-close-tag-modal') as HTMLButtonElement;
        this.tagModalTitle = document.getElementById('tag-modal-title') as HTMLElement;
        this.tagInputName = document.getElementById('tag-input-name') as HTMLInputElement;
        this.tagInputColor = document.getElementById('tag-input-color') as HTMLInputElement;
        this.btnSaveTag = document.getElementById('btn-save-tag') as HTMLButtonElement;

        this.trackEditModal = document.getElementById('track-edit-modal') as HTMLElement;
        this.trackEditTitle = document.getElementById('track-edit-title') as HTMLInputElement;
        this.trackEditBpm = document.getElementById('track-edit-bpm') as HTMLInputElement;
        this.trackEditTags = document.getElementById('track-edit-tags') as HTMLElement;
        this.trackEditSelectedTags = document.getElementById('track-edit-selected-tags') as HTMLElement;
        this.trackEditTagSearch = document.getElementById('track-edit-tag-search') as HTMLInputElement;
        this.btnCloseTrackEdit = document.getElementById('btn-close-track-edit') as HTMLButtonElement;
        this.btnCancelTrackEdit = document.getElementById('btn-cancel-track-edit') as HTMLButtonElement;
        this.btnSaveTrackEdit = document.getElementById('btn-save-track-edit') as HTMLButtonElement;

        this.addTracksModal = document.getElementById('add-tracks-modal') as HTMLElement;
        this.addTracksModalTitle = document.getElementById('add-tracks-modal-title') as HTMLElement;
        this.addTracksSearch = document.getElementById('add-tracks-search') as HTMLInputElement;
        this.addTracksSummary = document.getElementById('add-tracks-summary') as HTMLElement;
        this.addTracksResults = document.getElementById('add-tracks-results') as HTMLElement;
        this.btnCloseAddTracks = document.getElementById('btn-close-add-tracks') as HTMLButtonElement;
    }

    private initSubsystems(): void {
        const vizContainer = document.getElementById('visualizer-container') as HTMLElement;
        const vizCanvas = document.getElementById('visualizer-canvas') as HTMLCanvasElement;
        const btnToggleViz = document.getElementById('btn-toggle-visualizer') as HTMLButtonElement;
        const btnCloseViz = document.getElementById('btn-close-viz') as HTMLButtonElement;

        this.visualizer = new AudioVisualizer(
            this.audio,
            vizContainer,
            vizCanvas,
            btnToggleViz,
            this.btnFullscreen,
            () => this.isPlaying,
            () => this.scheduleSaveState()
        );

        btnToggleViz?.addEventListener('click', () => this.visualizer.toggle());
        btnCloseViz?.addEventListener('click', () => this.visualizer.toggle(false));

        this.shortcuts = new KeyboardShortcutsManager(
            {
                onPlayPause: () => this.togglePlayPause(),
                onNext: () => this.nextTrack(),
                onPrev: () => this.prevTrack(),
                onSeek: (seconds: number) => this.seekBy(seconds),
                onSeekPercent: (num: number) => this.seekToPercent(num),
                onVolumeChange: (delta: number) => this.adjustVolume(delta),
                onToggleMute: () => this.toggleMute(),
                onSpeedChange: (delta: number) => this.adjustSpeed(delta),
                onSpeedReset: () => this.setSpeed(1.0),
                onToggleShuffle: () => this.toggleShuffle(),
                onToggleRepeat: () => this.toggleRepeat(),
                onToggleVisualizer: () => this.visualizer.toggle(),
                onOpenShortcuts: () => this.openShortcutsModal(),
                onCloseModals: () => {
                    this.closeShortcutsModal();
                    this.closeTagModal();
                    this.closeTrackEditor();
                    this.closeAddTracksModal();
                    this.speedPopup.classList.remove('open');
                }
            },
            this.searchInput
        );
    }

    private async registerServiceWorker(): Promise<void> {
        if ('serviceWorker' in navigator) {
            try {
                await navigator.serviceWorker.register('/sw.js');
            } catch (err) {
                console.warn('ServiceWorker registration error:', err);
            }
        }
    }

    private isStandalone(): boolean {
        const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
        return window.matchMedia('(display-mode: standalone)').matches || navigatorWithStandalone.standalone === true;
    }

    private async installApp(): Promise<void> {
        if (!this.installPrompt) return;

        const prompt = this.installPrompt;
        await prompt.prompt();
        const choice = await prompt.userChoice;
        this.installPrompt = null;
        this.btnInstallApp.style.display = 'none';
        if (choice.outcome === 'accepted') showToast('Danceify installed.', 'success');
    }

    private async initializeApp(): Promise<void> {
        this.tags = await dbManager.getAllTags();
        const savedState = await dbManager.getAppState();
        this.restoreAppState(savedState);

        this.allTracks = await dbManager.getAllTracks();

        // If local database is empty, load tracks from server
        if (this.allTracks.length === 0) {
            await this.fetchDefaultTracksFromServer();
        }

        if (this.selectedTagId === 'untagged' && !this.allTracks.some(track => !track.tags || track.tags.length === 0)) {
            this.selectedTagId = 'all';
        }

        this.updateStatsAndCounts();
        this.updateHeroTitle();
        this.applyFiltersAndSort();

        this.directoryHandle = await dbManager.getDirectoryHandle();

        if (this.directoryHandle) {
            this.btnSelectDirLabel.textContent = this.directoryHandle.name;
            this.btnSyncDir.style.display = 'inline-flex';

            try {
                const permission = await this.directoryHandle.queryPermission({ mode: 'read' });
                if (permission === 'granted') {
                    const indexingStatus = await dbManager.getIndexingStatus();
                    if (indexingStatus && (indexingStatus.isIndexing || indexingStatus.pendingPaths.length > 0)) {
                        this.startDirectorySync(this.directoryHandle);
                    }
                }
            } catch (err) {
                console.warn('Could not query directory permissions:', err);
            }
        } else {
            this.btnSelectDirLabel.textContent = 'Select Music Directory';
            this.btnSyncDir.style.display = 'none';
        }

        // Restore song and timestamp
        const trackToLoad = (savedState.currentTrackId && this.allTracks.find(t => t.id === savedState.currentTrackId))
            || (this.allTracks.length > 0 ? this.allTracks[0] : null);

        if (trackToLoad) {
            const targetTime = savedState.currentTime || 0;
            const shouldPlay = savedState.isPlaying || false;
            this.loadTrackPreview(trackToLoad, targetTime, shouldPlay);
        }
    }

    private async fetchDefaultTracksFromServer(): Promise<void> {
        try {
            const response = await fetch('/api/tracks');
            const data: TracksApiResponse = await response.json();
            const tracks: Track[] = (data.tracks || []).map((t: any, idx: number) => ({
                id: t.id || ('track-' + (idx + 1)),
                path: t.filename,
                filename: t.filename,
                title: t.title,
                artist: t.artist || 'Dance Sessions',
                album: t.album || 'Dance Vault',
                durationMs: 0,
                bpm: null,
                tags: directoryIndexer.deriveDefaultTags(t.filename, t.filename, this.tags),
                size: t.size || 0,
                lastModified: Date.now(),
                ext: t.ext || '.ogg',
                handle: null,
                gradient: t.gradient || getDeterministicGradient(t.filename),
                favorite: false
            }));

            await dbManager.saveTracks(tracks);
            this.allTracks = tracks;
        } catch (err) {
            console.warn('Could not load default tracks from server:', err);
        }
    }

    public async promptSelectDirectory(): Promise<void> {
        if (!('showDirectoryPicker' in window) || typeof window.showDirectoryPicker !== 'function') {
            showToast('Your browser does not support folder selection. Please use Chrome, Edge, or a modern Chromium browser.', 'warning');
            return;
        }

        try {
            const handle = await window.showDirectoryPicker({ mode: 'read' });
            if (!handle) return;

            this.directoryHandle = handle;
            await dbManager.saveDirectoryHandle(handle);

            this.btnSelectDirLabel.textContent = handle.name;
            this.btnSyncDir.style.display = 'inline-flex';

            showToast('Selected directory ' + handle.name + '. Indexing music...');
            await this.startDirectorySync(handle);
        } catch (err: any) {
            if (err.name !== 'AbortError') {
                console.error('Error selecting directory:', err);
                showToast('Could not open folder.', 'warning');
            }
        }
    }

    public async startDirectorySync(dirHandle: FileSystemDirectoryHandle): Promise<void> {
        const permission = await dirHandle.queryPermission({ mode: 'read' });
        if (permission !== 'granted') {
            const requested = await dirHandle.requestPermission({ mode: 'read' });
            if (requested !== 'granted') {
                showToast('Folder permission denied.', 'warning');
                return;
            }
        }

        this.indexingBanner.style.display = 'flex';
        this.btnSyncDir.disabled = true;

        try {
            this.allTracks = await directoryIndexer.syncDirectory(dirHandle, (status: IndexingStatus) => {
                this.updateIndexingUI(status);
            });

            this.updateStatsAndCounts();
            this.applyFiltersAndSort();
            showToast('Finished indexing ' + this.allTracks.length + ' tracks.', 'success');
        } catch (err) {
            console.error('Sync error:', err);
            showToast('Error indexing directory.', 'warning');
        } finally {
            this.indexingBanner.style.display = 'none';
            this.btnSyncDir.disabled = false;
        }
    }

    private updateIndexingUI(status: IndexingStatus): void {
        if (status.isIndexing) {
            this.indexingBanner.style.display = 'flex';
            const percent = status.totalFiles > 0 ? Math.round((status.processedFiles / status.totalFiles) * 100) : 0;
            this.indexingProgressBar.style.width = percent + '%';
            this.indexingStatusText.textContent = status.currentFile
                ? 'Indexing: ' + status.currentFile + ' (' + status.processedFiles + '/' + status.totalFiles + ', ' + percent + '%)'
                : 'Scanning directory... (' + status.totalFiles + ' files found)';
        } else {
            this.indexingBanner.style.display = 'none';
        }
    }

    private restoreAppState(state: AppState): void {
        this.selectedTagId = state.selectedTagId || 'all';
        this.playbackTagId = state.playbackTagId || state.selectedTagId || 'all';
        this.volume = state.volume ?? 0.8;
        this.isMuted = state.isMuted ?? false;
        this.playbackRate = state.playbackRate ?? 1.0;
        this.repeatMode = state.repeatMode || 'off';
        this.isShuffle = state.isShuffle || false;
        this.activeSort = state.activeSort || 'default';
        this.viewMode = state.viewMode || 'list';
        this.searchInput.value = state.searchQuery || '';

        this.setVolume(this.volume);
        this.setSpeed(this.playbackRate, false);
        this.enforcePitchLock();
        this.sortSelect.value = this.activeSort;
        this.setViewMode(this.viewMode);
        this.visualizer.restore(state.isVisualizerOpen ?? false);

        if (this.isShuffle) this.btnShuffle.classList.add('active');
        this.updateRepeatUI();

    }

    public saveStateImmediately(): void {
        const curTime = this.isRestoringSeek
            ? this.targetSeekTime
            : (this.audio.currentTime || 0);

        const state: Partial<AppState> = {
            selectedTagId: this.selectedTagId,
            playbackTagId: this.playbackTagId,
            currentTrackId: this.currentTrackId,
            currentTime: curTime,
            isPlaying: this.isPlaying,
            volume: this.volume,
            isMuted: this.isMuted,
            playbackRate: this.playbackRate,
            repeatMode: this.repeatMode,
            isShuffle: this.isShuffle,
            isVisualizerOpen: this.visualizer.getIsOpen(),
            activeSort: this.activeSort,
            searchQuery: this.searchInput.value,
            viewMode: this.viewMode
        };
        dbManager.saveAppState(state);
    }

    public scheduleSaveState(): void {
        if (this.saveStateDebounceTimer) {
            window.clearTimeout(this.saveStateDebounceTimer);
        }
        this.saveStateDebounceTimer = window.setTimeout(() => {
            this.saveStateImmediately();
        }, 500);
    }

    public renderTagsSidebar(): void {
        this.tagNavList.innerHTML = '';
        document.querySelector('.sidebar-nav [data-filter="all"]')?.classList.toggle('active', this.selectedTagId === 'all');
        document.querySelector('.sidebar-nav [data-filter="favorites"]')?.classList.toggle('active', this.selectedTagId === 'favorites');

        const selectSidebarItem = (li: HTMLElement, tagId: string): void => {
            document.querySelectorAll('.sidebar-nav .nav-item').forEach(el => el.classList.remove('active'));
            li.classList.add('active');
            this.selectedTagId = tagId;
            this.updateHeroTitle();
            this.applyFiltersAndSort();
            this.scheduleSaveState();
        };

        this.tags.forEach(tag => {
            const count = this.allTracks.filter(t => t.tags && t.tags.includes(tag.id)).length;
            const li = document.createElement('li');
            li.className = 'nav-item ' + (this.selectedTagId === tag.id ? 'active' : '');
            li.setAttribute('data-tag-id', tag.id);
            li.title = tag.name;

            li.innerHTML = [
                '<span class="mood-dot" style="background: ' + tag.color + ';"></span>',
                '<span class="tag-label-text">' + escapeHtml(tag.name) + '</span>',
                '<button class="tag-edit-btn" title="Edit ' + escapeHtml(tag.name) + '">',
                '    <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2" fill="none"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4Z"/></svg>',
                '</button>',
                '<span class="badge">' + count + '</span>'
            ].join('');

            li.addEventListener('click', (event: MouseEvent) => {
                if ((event.target as HTMLElement).closest('.tag-edit-btn')) {
                    this.openTagModal(tag.id);
                    return;
                }
                selectSidebarItem(li, tag.id);
            });

            this.tagNavList.appendChild(li);
        });

        const untaggedCount = this.allTracks.filter(track => !track.tags || track.tags.length === 0).length;
        if (untaggedCount > 0) {
            const li = document.createElement('li');
            li.className = 'nav-item nav-item-untagged ' + (this.selectedTagId === 'untagged' ? 'active' : '');
            li.setAttribute('data-tag-id', 'untagged');
            li.title = 'Untagged';
            li.innerHTML = [
                '<svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none" aria-hidden="true">',
                '    <path d="M20.59 13.41 11 3.83V3H4v7h.83l9.58 9.59a2 2 0 0 0 2.82 0l3.36-3.36a2 2 0 0 0 0-2.82Z"/>',
                '    <line x1="5" y1="19" x2="19" y2="5"/>',
                '</svg>',
                '<span class="tag-label-text">Untagged</span>',
                '<span class="badge">' + untaggedCount + '</span>'
            ].join('');
            li.addEventListener('click', () => selectSidebarItem(li, 'untagged'));
            this.tagNavList.appendChild(li);
        } else if (this.selectedTagId === 'untagged') {
            this.selectedTagId = 'all';
            document.querySelector('.sidebar-nav [data-filter="all"]')?.classList.add('active');
            this.updateHeroTitle();
        }
    }

    public openTagModal(tagId: string | null = null): void {
        const tag = tagId ? this.tags.find(item => item.id === tagId) : null;
        this.editingTagId = tag?.id || null;
        this.tagModal.style.display = 'flex';
        this.tagModalTitle.textContent = tag ? 'Edit Dance Tag' : 'Create New Dance Tag';
        this.btnSaveTag.textContent = tag ? 'Save Changes' : 'Create Tag';
        this.tagInputName.value = tag?.name || '';
        this.tagInputColor.value = tag?.color || '#1ed760';
        this.updateTagColorPreset(this.tagInputColor.value);
        this.tagInputName.focus();
        if (tag) this.tagInputName.select();
    }

    private updateTagColorPreset(color: string): void {
        document.querySelectorAll<HTMLButtonElement>('.color-swatch').forEach(swatch => {
            swatch.classList.toggle('active', swatch.dataset.color?.toLowerCase() === color.toLowerCase());
        });
    }

    public closeTagModal(): void {
        this.tagModal.style.display = 'none';
        this.editingTagId = null;
    }

    public async saveNewTag(): Promise<void> {
        const name = this.tagInputName.value.trim();
        const color = this.tagInputColor.value || '#1ed760';
        if (!name) return;

        const existingTag = this.tags.find(tag => tag.id === this.editingTagId);
        if (existingTag) {
            existingTag.name = name;
            existingTag.color = color;
            await dbManager.saveTag(existingTag);
            this.renderTagsSidebar();
            this.updateHeroTitle();
            this.applyFiltersAndSort();
            this.closeTagModal();
            showToast('Updated tag ' + name, 'success');
            return;
        }

        const newTag: Tag = {
            id: 'tag-' + Date.now() + '-' + Math.random().toString(36).substring(2, 7),
            name: name,
            color: color,
            isDefault: false,
            createdAt: Date.now(),
            order: this.tags.length + 1
        };

        this.tags.push(newTag);
        await dbManager.saveTag(newTag);
        this.renderTagsSidebar();
        this.closeTagModal();
        showToast('Created tag ' + name);
    }

    public async toggleTrackTag(trackId: string, tagId: string): Promise<void> {
        const track = this.allTracks.find(t => t.id === trackId);
        if (!track) return;

        if (track.tags.includes(tagId)) {
            track.tags = track.tags.filter(t => t !== tagId);
        } else {
            track.tags.push(tagId);
        }

        await dbManager.saveTrack(track);
        this.renderTagsSidebar();
        this.applyFiltersAndSort();
    }

    public openAddTracksModal(): void {
        const tag = this.tags.find(item => item.id === this.selectedTagId);
        if (!tag) return;

        this.addTracksModalTitle.textContent = 'Add Tracks to ' + tag.name;
        this.addTracksSearch.value = '';
        this.renderAddTracksResults();
        this.addTracksModal.style.display = 'flex';
        this.addTracksSearch.focus();
    }

    public closeAddTracksModal(): void {
        this.addTracksModal.style.display = 'none';
        this.addTracksSearch.value = '';
    }

    private renderAddTracksResults(): void {
        const tag = this.tags.find(item => item.id === this.selectedTagId);
        this.addTracksResults.replaceChildren();
        if (!tag) {
            this.addTracksSummary.textContent = '';
            return;
        }

        const query = this.addTracksSearch.value.trim().toLowerCase();
        const available = this.allTracks.filter(track => {
            if (track.tags.includes(tag.id)) return false;
            if (!query) return true;
            return track.title.toLowerCase().includes(query) || track.filename.toLowerCase().includes(query);
        });

        this.addTracksSummary.textContent = available.length === 1
            ? '1 available track'
            : available.length + ' available tracks';

        if (available.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'add-tracks-empty';
            empty.textContent = query
                ? 'No unassigned tracks match your search.'
                : 'Every track is already in this list.';
            this.addTracksResults.appendChild(empty);
            return;
        }

        for (const track of available) {
            const row = document.createElement('div');
            row.className = 'add-track-result';

            const artwork = document.createElement('div');
            artwork.className = 'add-track-artwork';
            artwork.style.background = track.gradient;
            artwork.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6Z"/></svg>';

            const metadata = document.createElement('div');
            metadata.className = 'add-track-metadata';
            const title = document.createElement('span');
            title.className = 'add-track-title';
            title.textContent = track.title;
            const filename = document.createElement('span');
            filename.className = 'add-track-filename';
            filename.textContent = track.filename;
            metadata.append(title, filename);

            const addButton = document.createElement('button');
            addButton.type = 'button';
            addButton.className = 'add-track-result-btn';
            addButton.dataset.trackId = track.id;
            addButton.innerHTML = '<svg viewBox="0 0 24 24" width="15" height="15" stroke="currentColor" stroke-width="2" fill="none"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg><span>Add</span>';
            row.append(artwork, metadata, addButton);
            this.addTracksResults.appendChild(row);
        }
    }

    private async addTrackToSelectedTag(trackId: string): Promise<void> {
        const tag = this.tags.find(item => item.id === this.selectedTagId);
        const track = this.allTracks.find(item => item.id === trackId);
        if (!tag || !track || track.tags.includes(tag.id)) return;

        track.tags.push(tag.id);
        await dbManager.saveTrack(track);
        this.updateStatsAndCounts();
        this.applyFiltersAndSort();
        this.renderAddTracksResults();
        showToast('Added ' + track.title + ' to ' + tag.name, 'success');
    }

    public openTrackEditor(trackId: string): void {
        const track = this.allTracks.find(item => item.id === trackId);
        if (!track) return;

        this.editingTrackId = track.id;
        this.trackEditTitle.value = track.title;
        this.trackEditBpm.value = track.bpm === null ? '' : String(track.bpm);
        this.trackEditTagSearch.value = '';
        this.editingTrackTags = new Set(track.tags);
        this.renderTrackTagPicker();

        this.trackEditModal.style.display = 'flex';
        this.trackEditTitle.focus();
        this.trackEditTitle.select();
    }

    private renderTrackTagPicker(): void {
        this.trackEditSelectedTags.replaceChildren();
        const selectedTags = this.tags.filter(tag => this.editingTrackTags.has(tag.id));

        if (selectedTags.length === 0) {
            const empty = document.createElement('span');
            empty.className = 'track-tag-empty';
            empty.textContent = 'No tags selected';
            this.trackEditSelectedTags.appendChild(empty);
        } else {
            for (const tag of selectedTags) {
                const chip = document.createElement('button');
                chip.type = 'button';
                chip.className = 'track-tag-chip';
                chip.dataset.tagId = tag.id;
                chip.title = 'Remove ' + tag.name;

                const color = document.createElement('span');
                color.className = 'track-tag-color';
                color.style.backgroundColor = tag.color;

                const name = document.createElement('span');
                name.textContent = tag.name;

                const remove = document.createElement('svg');
                remove.setAttribute('viewBox', '0 0 24 24');
                remove.setAttribute('width', '14');
                remove.setAttribute('height', '14');
                remove.setAttribute('stroke', 'currentColor');
                remove.setAttribute('stroke-width', '2');
                remove.setAttribute('fill', 'none');
                remove.innerHTML = '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>';
                chip.append(color, name, remove);
                this.trackEditSelectedTags.appendChild(chip);
            }
        }

        this.trackEditTags.replaceChildren();
        const query = this.trackEditTagSearch.value.trim().toLowerCase();
        const availableTags = this.tags.filter(tag =>
            !this.editingTrackTags.has(tag.id) && tag.name.toLowerCase().includes(query)
        );

        if (availableTags.length === 0) {
            const empty = document.createElement('span');
            empty.className = 'track-tag-empty';
            empty.textContent = this.tags.length === 0
                ? 'Create a tag first to assign it here.'
                : query ? 'No matching tags' : 'All matching tags selected';
            this.trackEditTags.appendChild(empty);
            return;
        }

        for (const tag of availableTags) {
            const option = document.createElement('button');
            option.type = 'button';
            option.className = 'track-tag-option';
            option.dataset.tagId = tag.id;
            option.setAttribute('role', 'option');

            const color = document.createElement('span');
            color.className = 'track-tag-color';
            color.style.backgroundColor = tag.color;

            const name = document.createElement('span');
            name.textContent = tag.name;

            const add = document.createElement('svg');
            add.setAttribute('viewBox', '0 0 24 24');
            add.setAttribute('width', '15');
            add.setAttribute('height', '15');
            add.setAttribute('stroke', 'currentColor');
            add.setAttribute('stroke-width', '2');
            add.setAttribute('fill', 'none');
            add.innerHTML = '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>';
            option.append(color, name, add);
            this.trackEditTags.appendChild(option);
        }
    }

    public closeTrackEditor(): void {
        this.trackEditModal.style.display = 'none';
        this.editingTrackId = null;
        this.editingTrackTags.clear();
    }

    public async saveTrackEdits(): Promise<void> {
        const track = this.allTracks.find(item => item.id === this.editingTrackId);
        if (!track) return;

        const title = this.trackEditTitle.value.trim();
        const bpmText = this.trackEditBpm.value.trim();
        const bpm = bpmText === '' ? null : Number(bpmText);
        if (!title) {
            showToast('Track name is required.', 'warning');
            this.trackEditTitle.focus();
            return;
        }
        if (bpm !== null && (!Number.isInteger(bpm) || bpm < 40 || bpm > 260)) {
            showToast('BPM must be a whole number from 40 to 260.', 'warning');
            this.trackEditBpm.focus();
            return;
        }

        track.title = title;
        track.bpm = bpm;
        track.bpmSource = 'manual';
        track.tags = Array.from(this.editingTrackTags);
        await dbManager.saveTrack(track);

        this.updateStatsAndCounts();
        this.applyFiltersAndSort();
        if (this.currentTrackId === track.id) {
            this.playerTitle.textContent = track.title;
            this.playerArtist.textContent = track.artist + ' - ' + (track.bpm ? track.bpm + ' BPM' : track.album);
            const vizTitle = document.getElementById('viz-track-title');
            if (vizTitle) vizTitle.textContent = 'Playing: ' + track.title + (track.bpm ? ' (' + track.bpm + ' BPM)' : '');
        }
        this.closeTrackEditor();
        showToast('Saved changes to ' + track.title, 'success');
    }

    private async prepareTrackAudioSource(track: Track): Promise<string | null> {
        if (this.currentBlobUrl) {
            URL.revokeObjectURL(this.currentBlobUrl);
            this.currentBlobUrl = null;
        }

        if (track.handle) {
            try {
                const file = await track.handle.getFile();
                this.currentBlobUrl = URL.createObjectURL(file);
                return this.currentBlobUrl;
            } catch (err) {
                console.warn('Could not get file from handle for ' + track.filename + ':', err);
            }
        }

        return '/audio/' + encodeURIComponent(track.filename);
    }

    private loadTrackPreview(track: Track, seekTime: number = 0, autoPlay: boolean = false): void {
        this.currentTrackId = track.id;
        const idx = this.displayedTracks.findIndex(t => t.id === track.id);
        if (idx !== -1) {
            this.currentTrackIndex = idx;
        }

        this.isRestoringSeek = seekTime > 0;
        this.targetSeekTime = seekTime;
        this.shouldResumeOnFirstInteraction = autoPlay;
        this.isPlaying = autoPlay;

        this.playerTitle.textContent = track.title;
        this.playerArtist.textContent = track.artist + ' - ' + (track.bpm ? track.bpm + ' BPM' : track.album);
        this.playerCover.style.background = track.gradient;
        this.btnPlayerFav.classList.toggle('active', track.favorite);
        this.highlightActiveRow();

        if (seekTime > 0) {
            this.timeCurrent.textContent = formatTime(seekTime);
            if (track.durationMs > 0) {
                const durSec = track.durationMs / 1000;
                const percent = Math.min(100, Math.max(0, (seekTime / durSec) * 100));
                this.progressFill.style.width = percent + '%';
                this.timeTotal.textContent = formatTime(Math.round(durSec));
            }
        }

        this.prepareTrackAudioSource(track).then(src => {
            if (src) {
                this.audio.src = src;
                this.audio.playbackRate = this.playbackRate;
                this.enforcePitchLock();

                const applySeekAndPlay = () => {
                    if (seekTime > 0) {
                        try {
                            this.audio.currentTime = seekTime;
                            this.timeCurrent.textContent = formatTime(seekTime);
                        } catch (e) {
                            console.warn('Could not set currentTime on load:', e);
                        }
                    }
                    if (this.audio.duration && !isNaN(this.audio.duration) && isFinite(this.audio.duration)) {
                        this.timeTotal.textContent = formatTime(this.audio.duration);
                        if (seekTime > 0) {
                            const percent = (seekTime / this.audio.duration) * 100;
                            this.progressFill.style.width = Math.min(100, Math.max(0, percent)) + '%';
                        }
                    }

                    if (autoPlay) {
                        this.audio.play().then(() => {
                            this.shouldResumeOnFirstInteraction = false;
                            this.isPlaying = true;
                            this.updatePlayPauseUI();
                            document.body.classList.add('playing');
                            this.visualizer.initAudioContext();
                            this.isRestoringSeek = false;
                        }).catch((err) => {
                            console.log('Autoplay restriction on page reload, will resume on interaction:', err);
                            this.shouldResumeOnFirstInteraction = true;
                            this.setupAutoResumeListeners();
                        });
                    } else {
                        setTimeout(() => { this.isRestoringSeek = false; }, 400);
                    }
                };

                if (this.audio.readyState >= 1) {
                    applySeekAndPlay();
                } else {
                    this.audio.addEventListener('loadedmetadata', applySeekAndPlay, { once: true });
                    this.audio.addEventListener('canplay', applySeekAndPlay, { once: true });
                }
            }
        });
    }

    private setupAutoResumeListeners(): void {
        const resume = () => {
            if (this.shouldResumeOnFirstInteraction && this.audio.paused && this.audio.src) {
                this.audio.play().then(() => {
                    this.isPlaying = true;
                    this.updatePlayPauseUI();
                    document.body.classList.add('playing');
                    this.visualizer.initAudioContext();
                    this.isRestoringSeek = false;
                    this.shouldResumeOnFirstInteraction = false;
                }).catch(() => { });
            }
            window.removeEventListener('click', resume);
            window.removeEventListener('keydown', resume);
        };
        window.addEventListener('click', resume, { once: true });
        window.addEventListener('keydown', resume, { once: true });
    }

    public async playTrack(index: number, seekTime?: number): Promise<void> {
        if (index < 0 || index >= this.displayedTracks.length) return;
        const track = this.displayedTracks[index];
        this.playbackTagId = this.selectedTagId;
        await this.playTrackFromContext(track, seekTime);
    }

    private async playTrackFromContext(track: Track, seekTime?: number): Promise<void> {
        this.currentTrackIndex = this.displayedTracks.findIndex(item => item.id === track.id);
        const isSameTrack = this.currentTrackId === track.id && this.audio.src;
        this.currentTrackId = track.id;
        this.isRestoringSeek = false;
        this.shouldResumeOnFirstInteraction = false;

        if (!isSameTrack) {
            const src = await this.prepareTrackAudioSource(track);
            if (!src) {
                showToast('Could not open audio file.', 'warning');
                return;
            }
            this.audio.src = src;
        }

        this.audio.playbackRate = this.playbackRate;
        this.enforcePitchLock();

        if (typeof seekTime === 'number' && seekTime >= 0) {
            try {
                this.audio.currentTime = seekTime;
            } catch { }
        }

        try {
            await this.audio.play();
            this.isPlaying = true;
            this.updatePlayPauseUI();
            document.body.classList.add('playing');
            this.visualizer.initAudioContext();
        } catch (err) {
            console.warn('Playback error:', err);
        }

        this.playerTitle.textContent = track.title;
        this.playerArtist.textContent = track.artist + ' - ' + (track.bpm ? track.bpm + ' BPM' : track.album);
        this.playerCover.style.background = track.gradient;
        const vizTitle = document.getElementById('viz-track-title');
        if (vizTitle) vizTitle.textContent = 'Playing: ' + track.title + (track.bpm ? ' (' + track.bpm + ' BPM)' : '');

        this.updateFavoritesUI();
        this.highlightActiveRow();
        this.scheduleSaveState();
    }

    public togglePlayPause(): void {
        this.isRestoringSeek = false;
        this.shouldResumeOnFirstInteraction = false;

        if (this.currentTrackIndex === -1 && this.currentTrackId) {
            const idx = this.displayedTracks.findIndex(t => t.id === this.currentTrackId);
            if (idx !== -1) {
                this.currentTrackIndex = idx;
            }
        }

        if (this.currentTrackIndex === -1 && !this.audio.src) {
            if (this.displayedTracks.length > 0) {
                this.playTrack(0);
            }
            return;
        }

        if (this.audio.paused) {
            this.audio.play().then(() => {
                this.isPlaying = true;
                this.updatePlayPauseUI();
                document.body.classList.add('playing');
                this.visualizer.initAudioContext();
            }).catch(err => {
                console.warn('Play error:', err);
            });
            showToast('Playing', 'info', 'playback');
        } else {
            this.audio.pause();
            this.isPlaying = false;
            this.updatePlayPauseUI();
            document.body.classList.remove('playing');
            showToast('Paused', 'info', 'playback');
        }
        this.scheduleSaveState();
    }

    public prevTrack(): void {
        if (this.audio.currentTime > 3) {
            this.audio.currentTime = 0;
            return;
        }
        const queue = this.getPlaybackQueue();
        if (queue.length === 0) return;
        const currentIndex = queue.findIndex(track => track.id === this.currentTrackId);
        const previousIndex = currentIndex <= 0 ? queue.length - 1 : currentIndex - 1;
        this.playTrackFromContext(queue[previousIndex]);
    }

    public nextTrack(isAutoEnd: boolean = false): void {
        const queue = this.getPlaybackQueue();
        if (queue.length === 0) return;
        if (this.isShuffle) {
            const nextIndex = Math.floor(Math.random() * queue.length);
            this.playTrackFromContext(queue[nextIndex]);
            return;
        }

        const currentIndex = queue.findIndex(track => track.id === this.currentTrackId);
        let nextIndex = currentIndex + 1;
        if (nextIndex >= queue.length) {
            if (this.repeatMode === 'off' && isAutoEnd) {
                this.isPlaying = false;
                this.updatePlayPauseUI();
                return;
            }
            nextIndex = 0;
        }
        this.playTrackFromContext(queue[nextIndex]);
    }

    private getPlaybackQueue(): Track[] {
        let context = this.playbackTagId;
        if (context !== 'all' && context !== 'favorites' && context !== 'untagged' && !this.tags.some(tag => tag.id === context)) {
            context = 'all';
            this.playbackTagId = 'all';
        }

        const queue = this.allTracks.filter(track => {
            if (context === 'favorites') return track.favorite;
            if (context === 'untagged') return !track.tags || track.tags.length === 0;
            if (context === 'all') return true;
            return track.tags?.includes(context);
        });

        if (this.activeSort === 'title-asc') queue.sort((a, b) => a.title.localeCompare(b.title));
        else if (this.activeSort === 'title-desc') queue.sort((a, b) => b.title.localeCompare(a.title));
        else if (this.activeSort === 'bpm-asc') queue.sort((a, b) => (a.bpm || 999) - (b.bpm || 999));
        else if (this.activeSort === 'bpm-desc') queue.sort((a, b) => (b.bpm || 0) - (a.bpm || 0));
        else if (this.activeSort === 'duration') queue.sort((a, b) => b.durationMs - a.durationMs);
        return queue;
    }

    public showPlayingTrack(): void {
        const track = this.allTracks.find(item => item.id === this.currentTrackId);
        if (!track) return;

        let context = this.playbackTagId;
        const validContext = context === 'all'
            || (context === 'favorites' && track.favorite)
            || (context === 'untagged' && track.tags.length === 0)
            || (this.tags.some(tag => tag.id === context) && track.tags.includes(context));
        if (!validContext) context = track.tags[0] || (track.favorite ? 'favorites' : 'all');

        this.playbackTagId = context;
        this.selectedTagId = context;
        this.searchInput.value = '';
        this.clearSearchBtn.style.display = 'none';
        this.renderTagsSidebar();
        this.updateHeroTitle();
        this.applyFiltersAndSort();
        this.scheduleSaveState();

        const sidebarSelector = context === 'all' || context === 'favorites'
            ? '.sidebar-nav [data-filter="' + context + '"]'
            : '#tag-nav-list [data-tag-id="' + CSS.escape(context) + '"]';
        document.querySelector(sidebarSelector)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

        window.requestAnimationFrame(() => {
            const selector = '[data-track-id="' + CSS.escape(track.id) + '"]';
            const visibleTrack = this.viewMode === 'list'
                ? this.tracksTableBody.querySelector<HTMLElement>('tr' + selector)
                : this.tracksGrid.querySelector<HTMLElement>('.track-card' + selector);
            visibleTrack?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        });
    }

    public toggleShuffle(force: boolean | null = null): void {
        this.isShuffle = force !== null ? force : !this.isShuffle;
        this.btnShuffle.classList.toggle('active', this.isShuffle);
        showToast(this.isShuffle ? 'Shuffle: ON' : 'Shuffle: OFF', 'info', 'shuffle');
        this.scheduleSaveState();
    }

    public toggleRepeat(): void {
        if (this.repeatMode === 'off') {
            this.repeatMode = 'all';
            showToast('Repeat: ALL', 'info', 'repeat');
        } else if (this.repeatMode === 'all') {
            this.repeatMode = 'one';
            showToast('Repeat: ONE TRACK', 'info', 'repeat');
        } else {
            this.repeatMode = 'off';
            showToast('Repeat: OFF', 'info', 'repeat');
        }
        this.updateRepeatUI();
        this.scheduleSaveState();
    }

    private updateRepeatUI(): void {
        this.btnRepeat.classList.toggle('active', this.repeatMode !== 'off');
        this.repeatOneBadge.style.display = this.repeatMode === 'one' ? 'block' : 'none';
    }

    public updatePlayPauseUI(): void {
        if (this.isPlaying) {
            this.iconPlay.style.display = 'none';
            this.iconPause.style.display = 'block';
        } else {
            this.iconPlay.style.display = 'block';
            this.iconPause.style.display = 'none';
        }
    }

    public async toggleFavorite(trackId: string): Promise<void> {
        const track = this.allTracks.find(t => t.id === trackId);
        if (!track) return;

        track.favorite = !track.favorite;
        await dbManager.saveTrack(track);

        showToast(track.favorite ? 'Added to Favorites' : 'Removed from Favorites', track.favorite ? 'success' : 'info', 'favorite-' + track.id);
        this.updateStatsAndCounts();
        this.updateFavoritesUI();
        if (this.selectedTagId === 'favorites') {
            this.applyFiltersAndSort();
        }
    }

    public updateStatsAndCounts(): void {
        this.totalTracksBadge.textContent = String(this.allTracks.length);
        const favCount = this.allTracks.filter(t => t.favorite).length;
        this.favTracksBadge.textContent = String(favCount);
        this.renderTagsSidebar();
    }

    public updateFavoritesUI(): void {
        const curTrack = this.displayedTracks[this.currentTrackIndex] || this.allTracks.find(t => t.id === this.currentTrackId);
        if (curTrack) {
            this.btnPlayerFav.classList.toggle('active', curTrack.favorite);
        }
        document.querySelectorAll('.table-fav-btn').forEach(btn => {
            const id = btn.getAttribute('data-track-id');
            const track = this.allTracks.find(t => t.id === id);
            if (track) {
                btn.classList.toggle('active', track.favorite);
            }
        });
    }

    public applyFiltersAndSort(): void {
        const query = this.searchInput.value.toLowerCase().trim();

        const filtered = this.allTracks.filter(track => {
            if (this.selectedTagId === 'favorites') {
                if (!track.favorite) return false;
            } else if (this.selectedTagId === 'untagged') {
                if (track.tags && track.tags.length > 0) return false;
            } else if (this.selectedTagId !== 'all') {
                if (!track.tags || !track.tags.includes(this.selectedTagId)) return false;
            }

            if (query) {
                const matchTitle = track.title.toLowerCase().includes(query);
                const matchFile = track.filename.toLowerCase().includes(query);
                const matchArtist = track.artist.toLowerCase().includes(query);
                const matchBpm = track.bpm ? String(track.bpm).includes(query) : false;
                if (!matchTitle && !matchFile && !matchArtist && !matchBpm) return false;
            }

            return true;
        });

        if (this.activeSort === 'title-asc') {
            filtered.sort((a, b) => a.title.localeCompare(b.title));
        } else if (this.activeSort === 'title-desc') {
            filtered.sort((a, b) => b.title.localeCompare(a.title));
        } else if (this.activeSort === 'bpm-asc') {
            filtered.sort((a, b) => (a.bpm || 999) - (b.bpm || 999));
        } else if (this.activeSort === 'bpm-desc') {
            filtered.sort((a, b) => (b.bpm || 0) - (a.bpm || 0));
        } else if (this.activeSort === 'duration') {
            filtered.sort((a, b) => b.durationMs - a.durationMs);
        }

        this.displayedTracks = filtered;
        this.heroTrackCount.textContent = String(filtered.length) + ' tracks';

        if (this.currentTrackId) {
            const idx = this.displayedTracks.findIndex(t => t.id === this.currentTrackId);
            this.currentTrackIndex = idx;
        }

        this.renderTracks();
        this.highlightActiveRow();
    }

    public updateHeroTitle(): void {
        const selectedTag = this.tags.find(t => t.id === this.selectedTagId);
        this.btnAddTracksToTag.style.display = selectedTag ? 'inline-flex' : 'none';
        this.btnAddTracksToTag.title = selectedTag ? 'Add tracks to ' + selectedTag.name : '';
        this.heroCover.style.background = selectedTag
            ? 'linear-gradient(135deg, ' + selectedTag.color + ' 0%, #121212 100%)'
            : this.selectedTagId === 'favorites'
                ? 'linear-gradient(135deg, #e91e63 0%, #121212 100%)'
                : this.selectedTagId === 'untagged'
                    ? 'linear-gradient(135deg, #535353 0%, #121212 100%)'
                    : 'linear-gradient(135deg, #1db954 0%, #191414 100%)';

        if (this.selectedTagId === 'all') {
            this.heroPlaylistTitle.textContent = 'Dance Music Vault';
        } else if (this.selectedTagId === 'favorites') {
            this.heroPlaylistTitle.textContent = 'Your Favorite Tracks';
        } else if (this.selectedTagId === 'untagged') {
            this.heroPlaylistTitle.textContent = 'Untagged';
        } else {
            const tag = this.tags.find(t => t.id === this.selectedTagId);
            this.heroPlaylistTitle.textContent = tag ? tag.name : 'Dance Music Vault';
        }
    }

    public setViewMode(mode: ViewMode): void {
        this.viewMode = mode;
        this.viewListBtn.classList.toggle('active', mode === 'list');
        this.viewGridBtn.classList.toggle('active', mode === 'grid');
        this.tracksTable.style.display = mode === 'list' ? 'table' : 'none';
        this.tracksGrid.style.display = mode === 'grid' ? 'grid' : 'none';
        this.scheduleSaveState();
    }

    public renderTracks(): void {
        if (this.displayedTracks.length === 0) {
            this.tracksTable.style.display = 'none';
            this.tracksGrid.style.display = 'none';
            this.emptyState.style.display = 'flex';
            this.emptyMessage.textContent = this.allTracks.length === 0
                ? 'No music files found. Click "Select Music Directory" to load your dance collection!'
                : 'No tracks match your active filter/search.';
            return;
        }

        this.emptyState.style.display = 'none';
        this.tracksTable.style.display = this.viewMode === 'list' ? 'table' : 'none';
        this.tracksGrid.style.display = this.viewMode === 'grid' ? 'grid' : 'none';

        this.tracksTableBody.innerHTML = '';
        this.displayedTracks.forEach((track, idx) => {
            const tr = document.createElement('tr') as HTMLTableRowElement;
            tr.setAttribute('data-index', String(idx));
            tr.setAttribute('data-track-id', track.id);
            const isActive = this.currentTrackId ? track.id === this.currentTrackId : idx === this.currentTrackIndex;
            if (isActive) tr.classList.add('active-track');

            const trackTagNames = track.tags
                .map(tid => this.tags.find(t => t.id === tid))
                .filter((t): t is Tag => t !== undefined)
                .map(t => '<span class="dance-tag-badge" style="border-color: ' + t.color + '; color: ' + t.color + '; background: ' + t.color + '15;">' + escapeHtml(t.name) + '</span>')
                .join(' ');

            const durationFormatted = track.durationMs ? formatTime(Math.round(track.durationMs / 1000)) : formatFileSize(track.size);
            const bpmText = track.bpm ? '<span class="duration-bpm">' + track.bpm + ' BPM</span>' : '';

            tr.innerHTML = [
                '<td class="col-num">',
                '    <div class="track-index-wrapper">',
                '        <span class="track-index-num">' + (idx + 1) + '</span>',
                '        <svg class="row-play-icon" viewBox="0 0 24 24" width="14" height="14" fill="currentColor">',
                '            <polygon points="5 3 19 12 5 21 5 3"/>',
                '        </svg>',
                '    </div>',
                '</td>',
                '<td class="col-title">',
                '    <div class="track-title-cell">',
                '        <div class="track-thumb" style="background: ' + track.gradient + ';">',
                '            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">',
                '                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>',
                '            </svg>',
                '        </div>',
                '        <div class="track-name-group">',
                '            <span class="track-name">' + escapeHtml(track.title) + '</span>',
                '            <span class="track-filename">' + escapeHtml(track.filename) + '</span>',
                '        </div>',
                '    </div>',
                '</td>',
                '<td class="col-tag">',
                '    <div class="tag-badges-wrapper">',
                '        ' + (trackTagNames || '<span class="text-muted" style="font-size: 0.75rem;">Untagged</span>'),
                '    </div>',
                '</td>',
                '<td class="col-fav">',
                '    <div class="track-row-actions">',
                '    <button class="track-edit-btn" data-track-id="' + track.id + '" title="Edit track">',
                '        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4Z"/></svg>',
                '    </button>',
                '    <button class="table-fav-btn ' + (track.favorite ? 'active' : '') + '" data-track-id="' + track.id + '" title="Favorite">',
                '        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none">',
                '            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>',
                '        </svg>',
                '    </button>',
                '    </div>',
                '</td>',
                '<td class="col-duration"><span class="duration-value">' + durationFormatted + '</span>' + bpmText + '</td>'
            ].join('');

            tr.addEventListener('click', (e: MouseEvent) => {
                if ((e.target as HTMLElement).closest('.track-edit-btn')) {
                    this.openTrackEditor(track.id);
                    return;
                }
                if ((e.target as HTMLElement).closest('.table-fav-btn')) {
                    this.toggleFavorite(track.id);
                    return;
                }
                if (this.currentTrackId === track.id) {
                    this.togglePlayPause();
                } else {
                    this.playTrack(idx);
                }
            });

            this.tracksTableBody.appendChild(tr);
        });

        this.tracksGrid.innerHTML = '';
        this.displayedTracks.forEach((track, idx) => {
            const card = document.createElement('div');
            const isActive = this.currentTrackId ? track.id === this.currentTrackId : idx === this.currentTrackIndex;
            card.className = 'track-card ' + (isActive ? 'active-card' : '');
            card.setAttribute('data-index', String(idx));
            card.setAttribute('data-track-id', track.id);

            const durationFormatted = track.durationMs ? formatTime(Math.round(track.durationMs / 1000)) : formatFileSize(track.size);
            const bpmBadge = track.bpm ? '<span class="bpm-pill">' + track.bpm + ' BPM</span>' : '';

            card.innerHTML = [
                '<div class="card-cover" style="background: ' + track.gradient + ';">',
                '    <svg viewBox="0 0 24 24" width="48" height="48" fill="rgba(255,255,255,0.8)">',
                '        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>',
                '    </svg>',
                '    <button class="card-play-btn" title="Play">',
                '        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">',
                '            <polygon points="5 3 19 12 5 21 5 3"/>',
                '        </svg>',
                '    </button>',
                '    <button class="track-edit-btn card-edit-btn" title="Edit track">',
                '        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2" fill="none"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4Z"/></svg>',
                '    </button>',
                '</div>',
                '<div class="card-info">',
                '    <div class="card-title">' + escapeHtml(track.title) + '</div>',
                '    <div class="card-meta">',
                '        ' + bpmBadge,
                '        <span>' + durationFormatted + '</span>',
                '    </div>',
                '</div>'
            ].join('');

            card.addEventListener('click', (e: MouseEvent) => {
                if ((e.target as HTMLElement).closest('.track-edit-btn')) {
                    this.openTrackEditor(track.id);
                    return;
                }
                if (this.currentTrackId === track.id) {
                    this.togglePlayPause();
                } else {
                    this.playTrack(idx);
                }
            });

            this.tracksGrid.appendChild(card);
        });
    }

    public highlightActiveRow(): void {
        document.querySelectorAll('.tracks-table tbody tr').forEach((tr, i) => {
            const track = this.displayedTracks[i];
            const isActive = this.currentTrackId ? track?.id === this.currentTrackId : i === this.currentTrackIndex;
            tr.classList.toggle('active-track', Boolean(isActive));
        });
        document.querySelectorAll('.track-card').forEach((card, i) => {
            const track = this.displayedTracks[i];
            const isActive = this.currentTrackId ? track?.id === this.currentTrackId : i === this.currentTrackIndex;
            card.classList.toggle('active-card', Boolean(isActive));
        });
    }

    private bindEvents(): void {
        window.addEventListener('beforeinstallprompt', (event: Event) => {
            event.preventDefault();
            this.installPrompt = event as InstallPromptEvent;
            if (!this.isStandalone()) this.btnInstallApp.style.display = 'inline-flex';
        });
        window.addEventListener('appinstalled', () => {
            this.installPrompt = null;
            this.btnInstallApp.style.display = 'none';
        });
        this.btnInstallApp.addEventListener('click', () => {
            this.installApp().catch(error => console.warn('Could not install app:', error));
        });

        this.btnSelectDir.addEventListener('click', () => this.promptSelectDirectory());

        this.btnSyncDir.addEventListener('click', () => {
            if (this.directoryHandle) {
                this.startDirectorySync(this.directoryHandle);
            }
        });

        this.searchInput.addEventListener('input', () => {
            const query = this.searchInput.value.trim();
            this.clearSearchBtn.style.display = query.length > 0 ? 'block' : 'none';
            this.applyFiltersAndSort();
            this.scheduleSaveState();
        });

        this.clearSearchBtn.addEventListener('click', () => {
            this.searchInput.value = '';
            this.clearSearchBtn.style.display = 'none';
            this.applyFiltersAndSort();
            this.searchInput.focus();
            this.scheduleSaveState();
        });

        this.btnClearSearchEmpty.addEventListener('click', () => {
            this.searchInput.value = '';
            this.clearSearchBtn.style.display = 'none';
            this.selectedTagId = 'all';
            document.querySelectorAll('.sidebar-nav .nav-item').forEach(el => el.classList.remove('active'));
            document.querySelector('.sidebar-nav [data-filter="all"]')?.classList.add('active');
            this.applyFiltersAndSort();
            this.scheduleSaveState();
        });

        document.querySelector('.sidebar-nav [data-filter="all"]')?.addEventListener('click', (e) => {
            document.querySelectorAll('.sidebar-nav .nav-item').forEach(el => el.classList.remove('active'));
            (e.currentTarget as HTMLElement).classList.add('active');
            this.selectedTagId = 'all';
            this.updateHeroTitle();
            this.applyFiltersAndSort();
            this.scheduleSaveState();
        });

        document.querySelector('.sidebar-nav [data-filter="favorites"]')?.addEventListener('click', (e) => {
            document.querySelectorAll('.sidebar-nav .nav-item').forEach(el => el.classList.remove('active'));
            (e.currentTarget as HTMLElement).classList.add('active');
            this.selectedTagId = 'favorites';
            this.updateHeroTitle();
            this.applyFiltersAndSort();
            this.scheduleSaveState();
        });

        this.viewListBtn.addEventListener('click', () => this.setViewMode('list'));
        this.viewGridBtn.addEventListener('click', () => this.setViewMode('grid'));

        this.sortSelect.addEventListener('change', (e: Event) => {
            this.activeSort = (e.target as HTMLSelectElement).value as SortType;
            this.applyFiltersAndSort();
            this.scheduleSaveState();
        });

        this.btnAddTag.addEventListener('click', () => this.openTagModal());
        this.btnAddTracksToTag.addEventListener('click', () => this.openAddTracksModal());
        this.btnCloseAddTracks.addEventListener('click', () => this.closeAddTracksModal());
        this.addTracksSearch.addEventListener('input', () => this.renderAddTracksResults());
        this.addTracksResults.addEventListener('click', (e: MouseEvent) => {
            const button = (e.target as HTMLElement).closest<HTMLButtonElement>('.add-track-result-btn');
            if (button?.dataset.trackId) this.addTrackToSelectedTag(button.dataset.trackId);
        });
        this.btnCloseTagModal.addEventListener('click', () => this.closeTagModal());
        this.btnSaveTag.addEventListener('click', () => this.saveNewTag());
        this.btnCloseTrackEdit.addEventListener('click', () => this.closeTrackEditor());
        this.btnCancelTrackEdit.addEventListener('click', () => this.closeTrackEditor());
        this.btnSaveTrackEdit.addEventListener('click', () => this.saveTrackEdits());
        this.trackEditTagSearch.addEventListener('input', () => this.renderTrackTagPicker());
        this.trackEditTags.addEventListener('click', (e: MouseEvent) => {
            const option = (e.target as HTMLElement).closest<HTMLButtonElement>('.track-tag-option');
            if (!option?.dataset.tagId) return;
            this.editingTrackTags.add(option.dataset.tagId);
            this.trackEditTagSearch.value = '';
            this.renderTrackTagPicker();
            this.trackEditTagSearch.focus();
        });
        this.trackEditSelectedTags.addEventListener('click', (e: MouseEvent) => {
            const chip = (e.target as HTMLElement).closest<HTMLButtonElement>('.track-tag-chip');
            if (!chip?.dataset.tagId) return;
            this.editingTrackTags.delete(chip.dataset.tagId);
            this.renderTrackTagPicker();
            this.trackEditTagSearch.focus();
        });
        document.getElementById('tag-color-presets')?.addEventListener('click', (e: MouseEvent) => {
            const swatch = (e.target as HTMLElement).closest<HTMLButtonElement>('.color-swatch');
            if (!swatch?.dataset.color) return;
            this.tagInputColor.value = swatch.dataset.color;
            this.updateTagColorPreset(swatch.dataset.color);
        });
        this.tagInputColor.addEventListener('input', () => this.updateTagColorPreset(this.tagInputColor.value));

        this.btnPlayPause.addEventListener('click', () => this.togglePlayPause());
        this.btnPlayerTrackJump.addEventListener('click', () => this.showPlayingTrack());
        this.btnPlayAll.addEventListener('click', () => {
            if (this.currentTrackId && this.audio.src) {
                this.togglePlayPause();
            } else if (this.displayedTracks.length > 0) {
                this.playTrack(0);
            }
        });

        window.addEventListener('beforeunload', () => this.saveStateImmediately());
        window.addEventListener('pagehide', () => this.saveStateImmediately());

        this.btnPrev.addEventListener('click', () => this.prevTrack());
        this.btnNext.addEventListener('click', () => this.nextTrack());

        this.btnRewind5.addEventListener('click', () => this.seekBy(-5));
        this.btnRewind10.addEventListener('click', () => this.seekBy(-10));
        this.btnForward5.addEventListener('click', () => this.seekBy(5));
        this.btnForward10.addEventListener('click', () => this.seekBy(10));

        this.btnShuffle.addEventListener('click', () => this.toggleShuffle());
        this.btnHeroShuffle.addEventListener('click', () => {
            this.toggleShuffle(true);
            if (this.displayedTracks.length > 0) this.playTrack(0);
        });
        this.btnRepeat.addEventListener('click', () => this.toggleRepeat());

        this.setupScrubberEvents();

        this.btnSpeedMenu.addEventListener('click', (e: MouseEvent) => {
            e.stopPropagation();
            this.speedPopup.classList.toggle('open');
        });

        document.addEventListener('click', (e: MouseEvent) => {
            const target = e.target as Node;
            if (!this.speedPopup.contains(target) && target !== this.btnSpeedMenu) {
                this.speedPopup.classList.remove('open');
            }
        });

        this.speedSlider.addEventListener('input', (e: Event) => {
            this.setSpeed(parseFloat((e.target as HTMLInputElement).value));
        });

        this.btnSpeedMinus.addEventListener('click', () => this.adjustSpeed(-0.05));
        this.btnSpeedPlus.addEventListener('click', () => this.adjustSpeed(0.05));
        this.btnResetSpeedMini.addEventListener('click', () => this.setSpeed(1.0));

        document.querySelectorAll('.speed-chip, .preset-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const speed = parseFloat(btn.getAttribute('data-speed') || '1.0');
                this.setSpeed(speed);
            });
        });

        this.btnVolume.addEventListener('click', () => this.toggleMute());
        this.setupVolumeEvents();

        this.btnPlayerFav.addEventListener('click', () => {
            const currentTrack = this.displayedTracks[this.currentTrackIndex] || this.allTracks.find(t => t.id === this.currentTrackId);
            if (currentTrack) {
                this.toggleFavorite(currentTrack.id);
            }
        });

        this.btnFullscreen.addEventListener('click', () => this.visualizer.toggle());

        this.btnOpenShortcuts.addEventListener('click', () => this.openShortcutsModal());
        this.btnCloseShortcuts.addEventListener('click', () => this.closeShortcutsModal());
        this.btnModalGotit.addEventListener('click', () => this.closeShortcutsModal());
    }

    private setupAudioListeners(): void {
        this.audio.addEventListener('timeupdate', () => {
            this.onTimeUpdate();
        });

        this.audio.addEventListener('loadedmetadata', () => {
            if (this.audio.duration && isFinite(this.audio.duration)) {
                this.timeTotal.textContent = formatTime(this.audio.duration);
            }
        });

        this.audio.addEventListener('ended', () => {
            if (this.repeatMode === 'one') {
                this.audio.currentTime = 0;
                this.audio.play();
            } else {
                this.nextTrack(true);
            }
        });

        this.audio.addEventListener('play', () => {
            this.isPlaying = true;
            this.updatePlayPauseUI();
            document.body.classList.add('playing');
            this.visualizer.initAudioContext();
            this.scheduleSaveState();
        });

        this.audio.addEventListener('pause', () => {
            if (this.shouldResumeOnFirstInteraction) return;
            this.isPlaying = false;
            this.updatePlayPauseUI();
            document.body.classList.remove('playing');
            this.saveStateImmediately();
        });

        this.audio.addEventListener('error', (e: Event) => {
            console.error('Audio stream error:', e);
            showToast('Error streaming audio track', 'warning');
        });
    }

    private onTimeUpdate(): void {
        const cur = this.audio.currentTime || 0;
        const dur = this.audio.duration || 0;

        if (this.isRestoringSeek) {
            if (Math.abs(cur - this.targetSeekTime) < 0.8 || cur > this.targetSeekTime) {
                this.isRestoringSeek = false;
            } else {
                this.timeCurrent.textContent = formatTime(this.targetSeekTime);
                return;
            }
        }

        this.timeCurrent.textContent = formatTime(cur);

        if (dur > 0 && isFinite(dur)) {
            const percent = (cur / dur) * 100;
            this.progressFill.style.width = percent + '%';
        }

        this.scheduleSaveState();
    }

    private setupScrubberEvents(): void {
        let isDragging = false;

        const seekToPosition = (e: MouseEvent): void => {
            const rect = this.progressContainer.getBoundingClientRect();
            const clickX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
            const percent = clickX / rect.width;
            if (this.audio.duration) {
                this.audio.currentTime = percent * this.audio.duration;
            }
        };

        this.progressContainer.addEventListener('mousedown', (e: MouseEvent) => {
            isDragging = true;
            seekToPosition(e);
        });

        document.addEventListener('mousemove', (e: MouseEvent) => {
            if (isDragging) {
                seekToPosition(e);
            }
            if (this.progressContainer.matches(':hover') || isDragging) {
                const rect = this.progressContainer.getBoundingClientRect();
                const hoverX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
                const percent = hoverX / rect.width;
                if (this.audio.duration) {
                    const hoverTime = percent * this.audio.duration;
                    this.scrubTooltip.textContent = formatTime(hoverTime);
                    this.scrubTooltip.style.left = hoverX + 'px';
                    this.scrubTooltip.style.opacity = '1';
                }
            }
        });

        this.progressContainer.addEventListener('mouseleave', () => {
            if (!isDragging) this.scrubTooltip.style.opacity = '0';
        });

        document.addEventListener('mouseup', () => {
            if (isDragging) {
                isDragging = false;
                this.scrubTooltip.style.opacity = '0';
            }
        });
    }

    private setupVolumeEvents(): void {
        let isDraggingVol = false;

        const setVolFromEvent = (e: MouseEvent): void => {
            const rect = this.volumeBarContainer.getBoundingClientRect();
            const clickX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
            const vol = Math.max(0, Math.min(1, clickX / rect.width));
            this.setVolume(vol);
        };

        this.volumeBarContainer.addEventListener('mousedown', (e: MouseEvent) => {
            isDraggingVol = true;
            setVolFromEvent(e);
        });

        document.addEventListener('mousemove', (e: MouseEvent) => {
            if (isDraggingVol) {
                setVolFromEvent(e);
            }
        });

        document.addEventListener('mouseup', () => {
            if (isDraggingVol) {
                isDraggingVol = false;
            }
        });
    }

    public setVolume(val: number): void {
        this.volume = Math.max(0, Math.min(1, val));
        this.isMuted = this.volume === 0;
        this.audio.volume = this.volume;
        this.volumeFill.style.width = (this.volume * 100) + '%';
        this.iconVolHigh.style.display = this.isMuted ? 'none' : 'block';
        this.iconVolMute.style.display = this.isMuted ? 'block' : 'none';
        this.scheduleSaveState();
    }

    public toggleMute(): void {
        if (this.isMuted) {
            this.setVolume(this.prevVolume || 0.8);
            showToast('Unmuted (' + Math.round(this.volume * 100) + '%)', 'info', 'volume');
        } else {
            this.prevVolume = this.volume;
            this.setVolume(0);
            showToast('Muted', 'info', 'volume');
        }
    }

    public adjustVolume(delta: number): void {
        const newVol = Math.max(0, Math.min(1, this.volume + delta));
        this.setVolume(newVol);
        showToast('Volume: ' + Math.round(newVol * 100) + '%', 'info', 'volume');
    }

    public setSpeed(rate: number, announce: boolean = true): void {
        const clamped = Math.round(Math.max(0.25, Math.min(2.5, rate)) * 100) / 100;
        this.playbackRate = clamped;
        this.audio.playbackRate = clamped;

        const label = clamped.toFixed(2) + 'x';
        this.playerSpeedLabel.textContent = label;
        this.speedStepValue.textContent = label;
        this.speedSlider.value = String(clamped);
        const vizBadge = document.getElementById('viz-speed-badge');
        if (vizBadge) vizBadge.textContent = 'Speed: ' + label;

        document.querySelectorAll('.speed-chip, .preset-btn').forEach(btn => {
            const bSpeed = parseFloat(btn.getAttribute('data-speed') || '1.0');
            if (Math.abs(bSpeed - clamped) < 0.01) {
                btn.classList.add('active');
            } else {
                btn.classList.remove('active');
            }
        });

        if (announce) {
            showToast('Speed: ' + label, 'info', 'speed');
            this.scheduleSaveState();
        }
    }

    public adjustSpeed(delta: number): void {
        this.setSpeed(this.playbackRate + delta);
    }

    private enforcePitchLock(): void {
        this.audio.preservesPitch = true;
        this.audio.mozPreservesPitch = true;
        this.audio.webkitPreservesPitch = true;
    }

    public seekBy(seconds: number): void {
        if (!this.audio.duration) return;
        let newTime = (this.audio.currentTime || 0) + seconds;
        newTime = Math.max(0, Math.min(this.audio.duration, newTime));
        this.audio.currentTime = newTime;
        const sign = seconds > 0 ? '+' + seconds + 's' : seconds + 's';
        showToast(sign + ' (' + formatTime(newTime) + ')', 'info', 'seek');
    }

    public seekToPercent(num: number): void {
        if (!this.audio.duration) return;
        const target = (num / 10) * this.audio.duration;
        this.audio.currentTime = target;
        showToast('Jumped to ' + (num * 10) + '% (' + formatTime(target) + ')', 'info', 'seek');
    }

    public openShortcutsModal(): void {
        this.shortcutsModal.style.display = 'flex';
    }

    public closeShortcutsModal(): void {
        this.shortcutsModal.style.display = 'none';
    }
}

document.addEventListener('DOMContentLoaded', () => {
    (window as any).danceApp = new DancePlayerApp();
});
